import toast from 'react-hot-toast';
import { API_URL, apiFetch as fetch, loadPanels, readSessionUser } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PATIENT_STUDY_ENROLLMENT_ENABLED = process.env.REACT_APP_ENABLE_PATIENT_STUDY_ENROLLMENT === 'true';

const AnalyticsBars = ({ items, emptyMessage }) => {
  const maxValue = Math.max(...items.map(item => item.value), 0);

  if (items.length === 0) {
    return <p className="text-white/30 text-sm">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-4">
      {items.map(item => (
        <div key={item.label}>
          <div className="flex items-center justify-between gap-4 mb-2 text-sm">
            <span className="text-white/60 truncate">{item.label}</span>
            <span className="text-white font-mono shrink-0">{item.value.toLocaleString()}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#00d4aa] rounded-full"
              style={{ width: `${maxValue ? (item.value / maxValue) * 100 : 0}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ResearcherDashboard = () => {
  const [activeTab, setActiveTab] = useState('cohort');
  const [cohortCriteria, setCohortCriteria] = useState({
    cancerTypes: [],
    icdCodes: [],
    ageMin: '',
    ageMax: '',
    diseaseStages: [],
    treatmentTypes: [],
    minFollowup: '',
    dateRange: { start: '', end: '' },
  });
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [cohortResult, setCohortResult] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [savedCohorts, setSavedCohorts] = useState([]);
  const [studies, setStudies] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsScope, setAnalyticsScope] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showVariableSelector, setShowVariableSelector] = useState(false);
  const [isCreatingStudy, setIsCreatingStudy] = useState(false);
  const [recruitingActionId, setRecruitingActionId] = useState(null);
  const [expandedParticipants, setExpandedParticipants] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState(null);
  const [siteData, setSiteData] = useState(null);
  const [team, setTeam] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [collaborations, setCollaborations] = useState([]);
  const [showAddSite, setShowAddSite] = useState(false);
  const [regActionId, setRegActionId] = useState(null);
  const [extractionJobs, setExtractionJobs] = useState([]);
  const [extractActionId, setExtractActionId] = useState(null);
  const [obligations, setObligations] = useState([]);
  // Verdicts from /verify, keyed by release id. A digest printed beside a
  // release is only a claim until someone can re-derive it.
  const [releaseChecks, setReleaseChecks] = useState({});
  const navigate = useNavigate();

  // Cancer types with ICD-10 codes
  const cancerTypeOptions = [
    { name: 'Multiple Myeloma', icd: 'C90.0' },
    { name: 'DLBCL', icd: 'C83.3' },
    { name: 'AML', icd: 'C92.0' },
    { name: 'ALL', icd: 'C91.0' },
    { name: 'CLL', icd: 'C91.1' },
    { name: 'Hodgkin Lymphoma', icd: 'C81' },
    { name: 'Follicular Lymphoma', icd: 'C82' },
    { name: 'Mantle Cell Lymphoma', icd: 'C83.1' },
    { name: 'NSCLC', icd: 'C34' },
    { name: 'Breast Cancer', icd: 'C50' },
  ];

  const stageOptions = ['Stage I', 'Stage II', 'Stage III', 'Stage IV', 'Relapsed', 'Refractory', 'R-ISS I', 'R-ISS II', 'R-ISS III'];
  const treatmentOptions = [
    'Chemotherapy', 'Immunotherapy', 'CAR-T', 'Stem Cell Transplant', 
    'Radiation', 'Targeted Therapy', 'Bispecific Antibody', 'Proteasome Inhibitor',
    'IMiD', 'Anti-CD38', 'BCMA-Targeted'
  ];

  // Variable inventory measured from the records consented patients actually
  // contributed, loaded from the API. Nothing here is assumed.
  const [variableInventory, setVariableInventory] = useState(null);
  const variableCategories = (variableInventory?.categories || []).reduce((acc, c) => {
    acc[c.category] = c.variables;
    return acc;
  }, {});

  const tabs = [
    { id: 'cohort', label: 'Cohort Builder' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'studies', label: 'My Studies' },
    { id: 'regulatory', label: 'Regulatory Status' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const token = sessionStorage.getItem('token');
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      // Panels load independently: one unavailable endpoint should cost that
      // panel, not the whole dashboard.
      const { values, failures } = await loadPanels([
        () => fetch(`${API_URL}/api/cohort/saved`, auth),
        () => fetch(`${API_URL}/api/researcher/analytics`, auth),
        () => fetch(`${API_URL}/api/researcher/studies`, auth),
        () => fetch(`${API_URL}/api/researcher/collaborations`, auth),
        () => fetch(`${API_URL}/api/institutions`),
      ]);
      const [cohorts, analyticsData, studiesData, collabsData, instData] = values;

      setSavedCohorts(cohorts || []);
      setAnalytics(analyticsData);
      setStudies(studiesData || []);
      setCollaborations(collabsData || []);
      setInstitutions(instData || []);
      // Only say something went wrong if everything did. A partial load is a
      // working dashboard with a gap in it, not an error.
      if (failures.length === values.length) {
        setLoadError(failures[0].error?.message || 'Could not load your workspace.');
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Withdrawal obligations are not scoped to a study: a patient revoking
  // affects whichever releases carried them, so this is fetched once.
  const fetchObligations = useCallback(async () => {
    const token = sessionStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/researcher/release-obligations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setObligations(res.ok ? await res.json() : []);
    } catch (err) {
      console.error('Failed to fetch release obligations:', err);
      setObligations([]);
    }
  }, []);

  const fetchRegulatory = useCallback(async (studyId) => {
    if (!studyId) return;
    const token = sessionStorage.getItem('token');
    try {
      const [sitesRes, teamRes, jobsRes] = await Promise.all([
        fetch(`${API_URL}/api/researcher/studies/${studyId}/sites`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/study/${studyId}/team`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/extraction/jobs?study_id=${encodeURIComponent(studyId)}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSiteData(sitesRes.ok ? await sitesRes.json() : null);
      setTeam(teamRes.ok ? await teamRes.json() : []);
      setExtractionJobs(jobsRes.ok ? await jobsRes.json() : []);
    } catch (err) {
      console.error('Failed to fetch regulatory data:', err);
      setSiteData(null);
      setTeam([]);
      setExtractionJobs([]);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const user = (readSessionUser() || {});

    if (!token) {
      navigate('/login');
      return;
    }

    if (user.user_type === 'patient') {
      navigate('/patient');
      return;
    }

    fetchData();
    fetchObligations();
  }, [navigate, fetchData, fetchObligations]);

  const fetchAnalytics = useCallback(async (scope) => {
    const token = sessionStorage.getItem('token');
    const url = scope === 'all'
      ? `${API_URL}/api/researcher/analytics`
      : `${API_URL}/api/researcher/studies/${scope}/analytics`;
    setAnalytics(null);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAnalytics(await res.json());
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics(analyticsScope);
  }, [activeTab, analyticsScope, fetchAnalytics]);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/cohort/variables`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => (res.ok ? res.json() : null))
      .then(setVariableInventory)
      .catch(() => setVariableInventory(null));
  }, []);

  useEffect(() => {
    if (activeTab !== 'regulatory') return;
    const id = selectedStudyId || studies[0]?.id || collaborations[0]?.study_id;
    if (!id) return;
    if (!selectedStudyId) setSelectedStudyId(id);
    fetchRegulatory(id);
  }, [activeTab, selectedStudyId, studies, collaborations, fetchRegulatory]);

  const handleBuildCohort = async () => {
    setIsBuilding(true);
    const token = sessionStorage.getItem('token');
    const criteria = {
      cancer_types: cohortCriteria.cancerTypes,
      icd_codes: cohortCriteria.icdCodes,
      stages: cohortCriteria.diseaseStages,
      age_min: cohortCriteria.ageMin !== '' ? Number(cohortCriteria.ageMin) : null,
      age_max: cohortCriteria.ageMax !== '' ? Number(cohortCriteria.ageMax) : null,
      treatment_types: cohortCriteria.treatmentTypes,
      min_follow_up_months: cohortCriteria.minFollowup !== '' ? Number(cohortCriteria.minFollowup) : null,
      diagnosis_date_start: cohortCriteria.dateRange.start || null,
      diagnosis_date_end: cohortCriteria.dateRange.end || null,
    };

    try {
      const response = await fetch(`${API_URL}/api/cohort/build`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(criteria),
      });

      if (response.ok) {
        const data = await response.json();
        setCohortResult({
          patient_count: data.patient_count,
          data_points: data.data_points,
          diagnosis_count: data.diagnosis_count,
          treatment_count: data.treatment_count,
          molecular_count: data.molecular_count,
          available_institutions: data.available_institutions || [],
          data_completeness: data.data_completeness ?? 0,
          suppressed: data.suppressed,
          min_cell_size: data.min_cell_size,
          criteria,
        });
      } else {
        const errorData = await response.json();
        toast(`Failed to build cohort: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to build cohort:', error);
      toast.error(error.message);
    } finally {
      setIsBuilding(false);
    }
  };

  useEffect(() => { setCohortResult(null); }, [cohortCriteria]);

  const handleSaveCohort = async () => {
    if (!cohortResult) return;
    const name = prompt('Enter a name for this cohort:');
    if (!name) return;

    const token = sessionStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/cohort/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          description: `Cancer types: ${cohortCriteria.cancerTypes.join(', ') || 'Any'}`,
          criteria: cohortResult.criteria,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSavedCohorts(prev => [data, ...prev]);
        toast('Cohort saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save cohort:', error);
      toast.error(error.message);
    }
  };

  const toggleCancerType = (cancer) => {
    setCohortCriteria(prev => {
      const hasType = prev.cancerTypes.includes(cancer.name);
      return {
        ...prev,
        cancerTypes: hasType 
          ? prev.cancerTypes.filter(t => t !== cancer.name)
          : [...prev.cancerTypes, cancer.name],
        icdCodes: hasType
          ? prev.icdCodes.filter(c => c !== cancer.icd)
          : [...prev.icdCodes, cancer.icd],
      };
    });
  };

  const toggleCriteriaItem = (category, item) => {
    setCohortCriteria(prev => ({
      ...prev,
      [category]: prev[category].includes(item)
        ? prev[category].filter(i => i !== item)
        : [...prev[category], item],
    }));
  };

  const toggleVariable = (variable) => {
    setSelectedVariables(prev =>
      prev.includes(variable)
        ? prev.filter(v => v !== variable)
        : [...prev, variable]
    );
  };

  const handleCreateStudy = async (cohort = null) => {
    const name = prompt('Study name:', cohort?.name || '');
    if (!name) return;
    const description = prompt('Short description (optional):') || '';
    const principalInvestigator = prompt('Principal investigator (optional):') || '';

    setIsCreatingStudy(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/researcher/studies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          description,
          principal_investigator: principalInvestigator,
          cohort_id: cohort?.id || null,
        }),
      });

      if (response.ok) {
        await fetchData();
      } else {
        const data = await response.json();
        toast(data.detail || 'Failed to create study');
      }
    } catch (err) {
      console.error('Failed to create study:', err);
      toast('Failed to create study. Please try again.');
    } finally {
      setIsCreatingStudy(false);
    }
  };

  const handleToggleRecruiting = async (study) => {
    const nextIsRecruiting = !study.is_recruiting;
    let eligibilitySummary = study.eligibility_summary;
    if (nextIsRecruiting && !eligibilitySummary) {
      eligibilitySummary = prompt('Eligibility summary shown to patients (e.g. "Adults 18+ with Stage III/IV disease"):') || '';
    }

    setRecruitingActionId(study.id);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/researcher/studies/${study.id}/recruiting`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          is_recruiting: nextIsRecruiting,
          eligibility_summary: eligibilitySummary,
        }),
      });

      if (response.ok) {
        await fetchData();
      } else {
        const data = await response.json();
        toast(data.detail || 'Failed to update recruiting status');
      }
    } catch (err) {
      console.error('Failed to update recruiting status:', err);
    } finally {
      setRecruitingActionId(null);
    }
  };

  const handleToggleParticipants = async (studyId) => {
    if (expandedParticipants === studyId) {
      setExpandedParticipants(null);
      return;
    }
    setExpandedParticipants(studyId);
    setLoadingParticipants(true);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/researcher/studies/${studyId}/participants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setParticipants(await response.json());
        fetchData(); // refresh enrolled_count on the study card
      } else {
        setParticipants([]);
      }
    } catch (err) {
      console.error('Failed to fetch participants:', err);
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const handleAddSite = async (institutionId) => {
    setRegActionId(institutionId);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/researcher/studies/${selectedStudyId}/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ institution_id: institutionId }),
      });
      const data = await response.json();
      if (response.ok) {
        setShowAddSite(false);
        await fetchRegulatory(selectedStudyId);
        await fetchObligations();
      } else {
        toast(data.detail || 'Failed to add site');
      }
    } catch (err) {
      console.error('Failed to add site:', err);
    } finally {
      setRegActionId(null);
    }
  };

  const handleSubmitDoc = async (submissionId) => {
    setRegActionId(submissionId);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/regulatory/${submissionId}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        await fetchRegulatory(selectedStudyId);
        await fetchObligations();
      } else {
        toast(data.detail || 'Failed to submit document');
      }
    } catch (err) {
      console.error('Failed to submit document:', err);
    } finally {
      setRegActionId(null);
    }
  };

  const handleInviteCollaborator = async () => {
    const email = prompt('Collaborator email:');
    if (!email) return;
    const role = prompt('Role (co_investigator, analyst, statistician):', 'co_investigator');
    if (!role) return;

    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(
        `${API_URL}/api/study/${selectedStudyId}/invite?email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        await fetchRegulatory(selectedStudyId);
        await fetchObligations();
        toast(data.message);
      } else {
        toast(data.detail || 'Failed to invite collaborator');
      }
    } catch (err) {
      console.error('Failed to invite collaborator:', err);
    }
  };

  const handleRequestExtract = async () => {
    if (!selectedStudyId) return;
    if (!selectedVariables.length) {
      setShowVariableSelector(true);
      toast('Select variables, then request the extract again.');
      return;
    }
    setExtractActionId('create');
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/extraction/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          study_id: selectedStudyId,
          variables: selectedVariables,
          output_format: 'csv',
          deidentification_level: 'limited_dataset',
        }),
      });
      const data = await response.json();
      if (response.ok) {
        await fetchRegulatory(selectedStudyId);
        await fetchObligations();
      } else {
        toast(data.detail || 'Failed to request extract');
      }
    } catch (err) {
      console.error('Failed to request extract:', err);
      toast.error(err.message);
    } finally {
      setExtractActionId(null);
    }
  };

  const handleDownloadExtract = async (job) => {
    setExtractActionId(job.id);
    const token = sessionStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}${job.download_url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json();
        toast(data.detail || 'Failed to download extract');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${job.job_name}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download extract:', err);
      toast('Failed to download extract');
    } finally {
      setExtractActionId(null);
    }
  };

  const regulatoryStudies = [
    ...studies.map(s => ({ id: s.id, name: s.name, mine: true })),
    ...collaborations
      .filter(c => !studies.some(s => s.id === c.study_id))
      .map(c => ({ id: c.study_id, name: c.study_name, mine: false })),
  ];

  const docStatusStyle = (status) => {
    if (status === 'approved' || status === 'signed') return 'bg-[#00d4aa]/20 text-[#00d4aa]';
    if (status === 'submitted' || status === 'under_review') return 'bg-amber-500/20 text-amber-500';
    return 'bg-white/10 text-white/40';
  };

  const verifyRelease = async (releaseId) => {
    setReleaseChecks((prev) => ({ ...prev, [releaseId]: { state: 'checking' } }));
    try {
      const res = await fetch(`${API_URL}/api/releases/${releaseId}/verify`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      const data = await res.json();
      setReleaseChecks((prev) => ({ ...prev, [releaseId]: data }));
    } catch (err) {
      // Unreachable is not "fine", so it does not render as one.
      setReleaseChecks((prev) => ({
        ...prev,
        [releaseId]: { state: 'unchecked', explanation: 'The check could not be run. Try again.' },
      }));
    }
  };

  const jobStatusStyle = (status) => {
    if (status === 'completed') return 'bg-[#00d4aa]/20 text-[#00d4aa]';
    if (status === 'queued' || status === 'running') return 'bg-amber-500/20 text-amber-500';
    if (status === 'held_for_review') return 'bg-amber-500/20 text-amber-500';
    if (status === 'failed') return 'bg-red-500/20 text-red-400';
    return 'bg-white/10 text-white/40';
  };
  // A status is a word the system uses about itself. "HELD_FOR_REVIEW" tells
  // a researcher nothing except that something went wrong, which is the one
  // thing it does not mean.
  const jobStatusLabel = (status) =>
    status === 'held_for_review' ? 'Under review' : status;

  if (loadError) {
    return <div role="alert" className="max-w-3xl mx-auto px-6 py-20 text-white"><h1 className="text-2xl mb-4">Unable to load your workspace</h1><p>{loadError}</p><button onClick={fetchData} className="mt-5 underline">Try again</button></div>;
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Header */}
      <section className="py-12 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Researcher Portal</p>
              <h1 className="heading-display text-4xl md:text-5xl text-white/90">Research Dashboard</h1>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-white/40 text-xs mb-1">Active Studies</p>
                <p className="text-white font-mono text-lg">{studies.length}</p>
              </div>
              <div className="h-8 w-px bg-white/10"></div>
              <div className="text-right">
                <p className="text-white/40 text-xs mb-1">Saved Cohorts</p>
                <p className="text-white font-mono text-lg">{savedCohorts.length}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-xs uppercase tracking-wider transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-white border-b-2 border-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          {obligations.length > 0 && (
            <div className="card-glass p-6 mb-6 border border-amber-400/30" data-testid="release-obligations">
              <h3 className="text-sm uppercase tracking-wider text-amber-300 mb-2">
                Withdrawal required ({obligations.length})
              </h3>
              <p className="text-white/45 text-sm mb-4">
                A participant has revoked consent after these extracts were released.
                This system cannot reach a file you already hold, so the obligation is yours.
              </p>
              <div className="space-y-2">
                {obligations.map((item) => (
                  <div key={item.id} className="py-3 border-b border-white/5 last:border-b-0">
                    <p className="text-white/80 text-sm break-all">{item.study_name}</p>
                    <p className="text-white/35 text-xs mt-1">
                      Released {item.released_at ? new Date(item.released_at).toLocaleDateString() : 'unknown'}
                      {' • '}revoked {new Date(item.withdrawal_required_at).toLocaleDateString()}
                      {' • '}sha256:{(item.content_digest || '').slice(0, 12)}
                    </p>
                    <p className="text-amber-300/80 text-xs mt-1">{item.action_required}</p>
                    {/*
                      A digest beside a release is a claim until someone can
                      re-derive it. This re-hashes the stored extract and
                      compares. "Cannot be checked" renders as its own answer,
                      never as a tick: a release whose file is gone is not a
                      verified one.
                    */}
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => verifyRelease(item.id)}
                        disabled={releaseChecks[item.id]?.state === 'checking'}
                        className="px-3 py-1 border border-white/20 text-white/70 text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all disabled:opacity-50"
                      >
                        {releaseChecks[item.id]?.state === 'checking' ? 'Checking…' : 'Check this extract'}
                      </button>
                      {releaseChecks[item.id] && releaseChecks[item.id].state !== 'checking' && (
                        <span className={`text-xs ${
                          releaseChecks[item.id].state === 'verified'
                            ? 'text-[#00d4aa]'
                            : releaseChecks[item.id].state === 'mismatch'
                            ? 'text-red-400'
                            : 'text-amber-300/80'
                        }`}>
                          {releaseChecks[item.id].explanation}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* COHORT BUILDER TAB */}
            {activeTab === 'cohort' && (
              <motion.div key="cohort" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Criteria Panel */}
                  <div className="lg:col-span-2 space-y-8">
                    <div>
                      <h2 className="text-lg font-medium text-white mb-2">Build Your Cohort</h2>
                      <p className="text-white/40 text-sm">
                        Define inclusion/exclusion criteria against stored synthetic test profiles.
                      </p>
                    </div>

                    {/* Inclusion Criteria */}
                    <div className="card-glass p-6">
                      <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Inclusion Criteria</h3>
                      
                      {/* Cancer Types with ICD-10 */}
                      <div className="mb-6">
                        <label className="block text-xs text-white/50 mb-3">Diagnosis (ICD-10)</label>
                        <div className="flex flex-wrap gap-2">
                          {cancerTypeOptions.map((cancer) => (
                            <button
                              key={cancer.icd}
                              onClick={() => toggleCancerType(cancer)}
                              className={`px-3 py-1.5 text-xs transition-all flex items-center gap-2 ${
                                cohortCriteria.cancerTypes.includes(cancer.name)
                                  ? 'bg-white text-black'
                                  : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                              }`}
                            >
                              <span>{cancer.name}</span>
                              <span className="opacity-50">({cancer.icd})</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Age Range */}
                      <div className="mb-6">
                        <label className="block text-xs text-white/50 mb-3">Age Range</label>
                        <div className="flex items-center gap-4">
                          <input
                            type="number"
                            placeholder="Min"
                            value={cohortCriteria.ageMin}
                            onChange={(e) => setCohortCriteria(prev => ({ ...prev, ageMin: e.target.value }))}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-white/30 focus:outline-none"
                          />
                          <span className="text-white/30 text-sm">to</span>
                          <input
                            type="number"
                            placeholder="Max"
                            value={cohortCriteria.ageMax}
                            onChange={(e) => setCohortCriteria(prev => ({ ...prev, ageMax: e.target.value }))}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-white/30 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Treatments */}
                      <div className="mb-6">
                        <label className="block text-xs text-white/50 mb-3">Treatment Exposure</label>
                        <div className="flex flex-wrap gap-2">
                          {treatmentOptions.map((treatment) => (
                            <button
                              key={treatment}
                              onClick={() => toggleCriteriaItem('treatmentTypes', treatment)}
                              className={`px-3 py-1.5 text-xs transition-all ${
                                cohortCriteria.treatmentTypes.includes(treatment)
                                  ? 'bg-white text-black'
                                  : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                              }`}
                            >
                              {treatment}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Stages */}
                      <div className="mb-6">
                        <label className="block text-xs text-white/50 mb-3">Disease Stage</label>
                        <div className="flex flex-wrap gap-2">
                          {stageOptions.map((stage) => (
                            <button
                              key={stage}
                              onClick={() => toggleCriteriaItem('diseaseStages', stage)}
                              className={`px-3 py-1.5 text-xs transition-all ${
                                cohortCriteria.diseaseStages.includes(stage)
                                  ? 'bg-white text-black'
                                  : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                              }`}
                            >
                              {stage}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Date Range */}
                      <div className="mb-6">
                        <label className="block text-xs text-white/50 mb-3">Diagnosis Date Range</label>
                        <div className="flex items-center gap-3">
                          <input
                            type="date"
                            value={cohortCriteria.dateRange.start}
                            onChange={(e) => setCohortCriteria(prev => ({
                              ...prev,
                              dateRange: { ...prev.dateRange, start: e.target.value }
                            }))}
                            className="min-w-0 flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:border-white/30 focus:outline-none"
                          />
                          <span className="text-white/30 text-sm shrink-0">to</span>
                          <input
                            type="date"
                            value={cohortCriteria.dateRange.end}
                            onChange={(e) => setCohortCriteria(prev => ({
                              ...prev,
                              dateRange: { ...prev.dateRange, end: e.target.value }
                            }))}
                            className="min-w-0 flex-1 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm focus:border-white/30 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Min Follow-up */}
                      <div>
                        <label className="block text-xs text-white/50 mb-3">Minimum Follow-up</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="e.g., 12"
                            value={cohortCriteria.minFollowup}
                            onChange={(e) => setCohortCriteria(prev => ({ ...prev, minFollowup: e.target.value }))}
                            className="w-20 px-3 py-2 bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-white/30 focus:outline-none"
                          />
                          <span className="text-white/30 text-sm">months</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={handleBuildCohort}
                        disabled={isBuilding}
                        className="px-8 py-4 bg-white text-black text-xs uppercase tracking-wider font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        {isBuilding ? 'Running Query...' : 'Run Feasibility'}
                      </button>
                      {cohortResult && cohortResult.patient_count > 0 && (
                        <button
                          onClick={() => setShowVariableSelector(true)}
                          className="px-8 py-4 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
                        >
                          Select Variables →
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Results Panel */}
                  <div className="lg:col-span-1">
                    <div className="card-glass p-6 sticky top-24">
                      <h3 className="text-lg font-medium text-white mb-6">Feasibility Results</h3>
                      
                      {cohortResult ? (
                        <div className="space-y-6">
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Matching Test Profiles</p>
                            <p className="text-4xl font-light text-white font-mono">
                              {cohortResult.patient_count.toLocaleString()}
                            </p>
                            <p className="text-white/30 text-sm mt-1">
                              synthetic profiles matching your criteria
                            </p>
                            {cohortResult.suppressed && (
                              <p className="text-amber-400 text-xs mt-2">
                                A test cohort exists but is smaller than the {cohortResult.min_cell_size}-profile
                                reporting floor, so the exact count is withheld. Broaden your criteria.
                              </p>
                            )}
                          </div>

                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Data Completeness</p>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-[#00d4aa]" 
                                  style={{ width: `${cohortResult.data_completeness * 100}%` }}
                                />
                              </div>
                              <span className="text-[#00d4aa] font-mono text-sm">
                                {Math.round(cohortResult.data_completeness * 100)}%
                              </span>
                            </div>
                          </div>

                          {cohortResult.available_institutions.length > 0 && (
                            <div>
                              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">
                                Synthetic institution fixtures
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {cohortResult.available_institutions.map((inst) => (
                                  <div key={inst} className="card-glass p-3 text-center">
                                    <p className="text-white/60 text-xs">{inst}</p>
                                  </div>
                                ))}
                              </div>
                              <p className="text-white/30 text-xs mt-2">
                                Pilot records are synthetic and are not attributable to a real source institution.
                              </p>
                            </div>
                          )}

                          <div className="pt-4 border-t border-white/10 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-white/40">Diagnoses</span>
                              <span className="text-white font-mono">{cohortResult.diagnosis_count}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/40">Treatments</span>
                              <span className="text-white font-mono">{cohortResult.treatment_count}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-white/40">Molecular</span>
                              <span className="text-white font-mono">{cohortResult.molecular_count}</span>
                            </div>
                          </div>

                          {cohortResult.patient_count > 0 && (
                            <div className="pt-4 space-y-3">
                              <button 
                                onClick={handleSaveCohort}
                                className="w-full py-3 bg-[#00d4aa] text-black text-xs uppercase tracking-wider font-medium hover:bg-[#00d4aa]/90 transition-colors"
                              >
                                Save Cohort
                              </button>
                              <button 
                                className="w-full py-3 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
                              >
                                Open governance simulation
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-white/30 text-sm">
                            Define criteria and run feasibility to see patient counts
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === 'analytics' && (
              <motion.div key="analytics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="mb-8">
                  <h2 className="text-lg font-medium text-white mb-2">Research Analytics</h2>
                  <p className="text-white/40 text-sm">Aggregate pilot insights across synthetic records with a recorded test acknowledgement.</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  <button
                    onClick={() => setAnalyticsScope('all')}
                    className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                      analyticsScope === 'all' ? 'bg-white text-black' : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                    }`}
                  >
                    All Consented Data
                  </button>
                  {studies.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setAnalyticsScope(s.id)}
                      className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                        analyticsScope === s.id ? 'bg-white text-black' : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>

                {!analytics ? (
                  <div className="card-glass p-8 text-center">
                    <p className="text-white/40 text-sm">Loading aggregate analytics...</p>
                  </div>
                ) : analytics.total_patients === 0 ? (
                  <div className="card-glass p-8 text-center">
                    <p className="text-white/40">No consented data yet.</p>
                    <p className="text-white/30 text-sm mt-2">
                      Aggregate analytics appear once synthetic records exist under a recorded test acknowledgement.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        ['Synthetic Profiles', analytics.total_patients],
                        ['Data Records', analytics.total_records],
                        ['Diagnosis Types', analytics.diagnoses.length],
                      ].map(([label, value]) => (
                        <div key={label} className="card-glass p-6">
                          <p className="text-sm uppercase tracking-wider text-white/40 mb-3">{label}</p>
                          <p className="text-3xl font-light text-white font-mono">{value.toLocaleString()}</p>
                        </div>
                      ))}
                      <div className="card-glass p-6 flex items-center">
                        <p className="text-white/30 text-sm">Counts reflect distinct synthetic test profiles, not real patients or clinical event volume.</p>
                      </div>
                    </div>

                    <div className="card-glass p-6">
                      <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Data by Category</h3>
                      <AnalyticsBars
                        items={Object.entries(analytics.records_by_category).map(([label, value]) => ({ label, value }))}
                        emptyMessage="No extracted data categories yet."
                      />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="card-glass p-6">
                        <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Top Diagnoses</h3>
                        <AnalyticsBars
                          items={analytics.diagnoses.map(item => ({ label: item.label, value: item.patient_count }))}
                          emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                        />
                      </div>

                      <div className="card-glass p-6">
                        <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Treatments</h3>
                        <AnalyticsBars
                          items={analytics.treatments.map(item => ({ label: item.label, value: item.patient_count }))}
                          emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                        />
                      </div>
                    </div>

                    <div className="card-glass p-6">
                      <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Demographics</h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Age Bands</p>
                          <AnalyticsBars
                            items={Object.entries(analytics.age_bands).map(([label, value]) => ({ label, value }))}
                            emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                          />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Sex</p>
                          <AnalyticsBars
                            items={Object.entries(analytics.sex).map(([label, value]) => ({ label, value }))}
                            emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="card-glass p-6">
                      <h3 className="text-sm uppercase tracking-wider text-white/40 mb-6">Real-World Outcomes</h3>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Treatment Response</p>
                          <AnalyticsBars
                            items={(analytics.responses || []).map(item => ({ label: item.label, value: item.patient_count }))}
                            emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                          />
                        </div>
                        <div>
                          <p className="text-white/50 text-xs uppercase tracking-wider mb-4">Vital Status</p>
                          <AnalyticsBars
                            items={Object.entries(analytics.vital_status || {}).map(([label, value]) => ({ label, value }))}
                            emptyMessage={`Not enough data to display groups (min ${analytics.min_cell_size} patients per group).`}
                          />
                        </div>
                      </div>
                    </div>

                    <p className="text-white/30 text-xs">
                      Groups with fewer than {analytics.min_cell_size} patients are hidden to protect privacy.
                      {analytics.suppressed_groups > 0 && ` ${analytics.suppressed_groups} group(s) hidden.`}
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* STUDIES TAB */}
            {activeTab === 'studies' && (
              <motion.div key="studies" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                  <div>
                    <h2 className="text-lg font-medium text-white mb-2">My Studies</h2>
                    <p className="text-white/40 text-sm">Track your research studies and saved cohorts</p>
                  </div>
                  <button
                    onClick={() => handleCreateStudy()}
                    disabled={isCreatingStudy}
                    className="px-6 py-3 bg-white text-black text-xs uppercase tracking-wider font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 shrink-0 self-start sm:self-auto"
                  >
                    {isCreatingStudy ? 'Creating...' : '+ New Study'}
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border border-white/20 border-t-white/60 rounded-full animate-spin mx-auto mb-4"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Studies */}
                    {studies.length > 0 ? studies.map((study) => (
                      <div key={study.id} className="card-glass p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-white font-medium">{study.name}</h3>
                              <a href={`/projects?study=${encodeURIComponent(study.id)}`} className="text-emerald-300 underline text-sm">Open shared project plan</a>
                              <a href={`/research-readiness?study=${encodeURIComponent(study.id)}`} className="text-emerald-300 underline text-sm">Review launch readiness</a>
                              <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 uppercase">
                                {study.status.replace('_', ' ')}
                              </span>
                              <span className={`px-2 py-0.5 text-xs uppercase ${PATIENT_STUDY_ENROLLMENT_ENABLED && study.is_recruiting ? 'bg-[#00d4aa]/20 text-[#00d4aa]' : 'bg-white/10 text-white/40'}`}>
                                {PATIENT_STUDY_ENROLLMENT_ENABLED ? (study.is_recruiting ? 'Simulation open' : 'Simulation closed') : 'Enrollment unavailable'}
                              </span>
                            </div>
                            <p className="text-white/40 text-sm">{study.enrolled_count} pilot participant record{study.enrolled_count === 1 ? '' : 's'}</p>
                            {study.eligibility_summary && (
                              <p className="text-white/30 text-xs mt-2 max-w-xl">{study.eligibility_summary}</p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 shrink-0">
                            <button
                              onClick={() => handleToggleRecruiting(study)}
                              disabled={!PATIENT_STUDY_ENROLLMENT_ENABLED || recruitingActionId === study.id}
                              className="px-4 py-2 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all disabled:opacity-50"
                            >
                              {!PATIENT_STUDY_ENROLLMENT_ENABLED ? 'Enrollment unavailable' : study.is_recruiting ? 'Close simulation' : 'Open simulation'}
                            </button>
                            {PATIENT_STUDY_ENROLLMENT_ENABLED && (
                              <button
                                onClick={() => handleToggleParticipants(study.id)}
                                className="px-4 py-2 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all"
                              >
                                {expandedParticipants === study.id ? 'Hide Test Participants' : 'View Test Participants'}
                              </button>
                            )}
                          </div>
                        </div>

                        {expandedParticipants === study.id && (
                          <div className="pt-4 border-t border-white/10">
                            <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Pilot Participant Records</p>
                            {loadingParticipants ? (
                              <p className="text-white/30 text-sm">Loading...</p>
                            ) : participants.length > 0 ? (
                              <div className="space-y-2">
                                {participants.map((p) => (
                                  <div key={p.id} className="flex items-center justify-between text-sm">
                                    <span className="text-white/60 font-mono">{p.patient_ref}</span>
                                    <div className="flex items-center gap-3">
                                      <span className={`px-2 py-0.5 text-xs uppercase ${p.status === 'enrolled' ? 'bg-[#00d4aa]/20 text-[#00d4aa]' : 'bg-white/10 text-white/40'}`}>
                                        {p.status}
                                      </span>
                                      <span className="text-white/30 text-xs">{p.enrolled_at ? new Date(p.enrolled_at).toLocaleDateString() : ''}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/30 text-sm">No pilot participant records exist.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )) : (
                      <div className="card-glass p-8 text-center">
                        <p className="text-white/40">No studies yet</p>
                        <p className="text-white/30 text-sm mt-2">Create a synthetic study record to evaluate planning and governance workflows.</p>
                      </div>
                    )}

                    {/* Saved Cohorts */}
                    <h3 className="text-white/40 text-xs uppercase tracking-wider pt-8 pb-4">Saved Cohorts</h3>
                    {savedCohorts.length > 0 ? (
                      savedCohorts.map((cohort) => (
                        <div key={cohort.id} className="card-glass card-hover p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <h4 className="text-white font-medium">{cohort.name}</h4>
                              <p className="text-white/40 text-sm">{cohort.description}</p>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                              <span className="text-white font-mono">{cohort.patient_count} pts</span>
                              <button
                                onClick={() => handleCreateStudy(cohort)}
                                disabled={isCreatingStudy}
                                className="px-3 py-1 border border-white/20 text-white/60 text-xs hover:bg-white hover:text-black transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {isCreatingStudy ? 'Creating…' : 'Convert to Study'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="card-glass p-8 text-center">
                        <p className="text-white/40">No saved cohorts yet</p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* REGULATORY TAB */}
            {activeTab === 'regulatory' && (
              <motion.div key="regulatory" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="mb-8">
                  <h2 className="text-lg font-medium text-white mb-2">Multicenter Workflow Simulation</h2>
                  <p className="text-white/40 text-sm">Exercise test site, review-state, agreement, and collaborator records. These are not real approvals or agreements.</p>
                </div>

                {regulatoryStudies.length === 0 ? (
                  <div className="card-glass p-8 text-center">
                    <p className="text-white/40">No studies yet</p>
                    <p className="text-white/30 text-sm mt-2">Create a study in the My Studies tab, then manage its sites and regulatory documents here.</p>
                  </div>
                ) : (
                  <>
                    {/* Study selector */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {regulatoryStudies.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setSelectedStudyId(s.id)}
                          className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                            selectedStudyId === s.id
                              ? 'bg-white text-black'
                              : 'bg-transparent text-white/50 border border-white/20 hover:border-white/40'
                          }`}
                        >
                          {s.name}{!s.mine && ' (shared)'}
                        </button>
                      ))}
                    </div>

                    {/* Central IRB + Sites */}
                    <div className="card-glass p-6 mb-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm uppercase tracking-wider text-white/40">Regulatory Status</h3>
                        {regulatoryStudies.find(s => s.id === selectedStudyId)?.mine && (
                          <button
                            onClick={() => setShowAddSite(!showAddSite)}
                            className="px-4 py-2 bg-white text-black text-xs uppercase tracking-wider font-medium hover:bg-gray-100 transition-colors"
                          >
                            + Add Site
                          </button>
                        )}
                      </div>

                      {showAddSite && (
                        <div className="mb-6 p-4 border border-white/10">
                          <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Add a participating institution</p>
                          <div className="flex flex-wrap gap-2">
                            {institutions
                              .filter(inst => !(siteData?.sites || []).some(s => s.institution_id === inst.id))
                              .map(inst => (
                                <button
                                  key={inst.id}
                                  onClick={() => handleAddSite(inst.id)}
                                  disabled={regActionId === inst.id}
                                  className="px-3 py-1.5 text-xs bg-transparent text-white/50 border border-white/20 hover:border-white/40 transition-all disabled:opacity-50"
                                >
                                  {regActionId === inst.id ? 'Adding...' : `${inst.name} (${inst.city}, ${inst.state})`}
                                </button>
                              ))}
                          </div>
                          <p className="text-white/30 text-xs mt-3">Adding a synthetic site creates test reliance, DUA, and central-review records for interface evaluation only.</p>
                        </div>
                      )}

                      {/* Central documents */}
                      {(siteData?.central || []).map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between py-3 border-b border-white/5">
                          <div>
                            <p className="text-white font-medium capitalize">
                              {String(doc.document_type || 'document').replace(/_/g, ' ')}
                            </p>
                            {doc.protocol_number && <p className="text-white/40 text-sm">Protocol #{doc.protocol_number}</p>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 text-xs uppercase ${docStatusStyle(doc.status)}`}>{doc.status.replace(/_/g, ' ')}</span>
                            {doc.status === 'draft' && regulatoryStudies.find(s => s.id === selectedStudyId)?.mine && (
                              <button
                                onClick={() => handleSubmitDoc(doc.id)}
                                disabled={regActionId === doc.id}
                                className="px-3 py-1 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all disabled:opacity-50"
                              >
                                Submit
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Sites */}
                      {(siteData?.sites || []).length > 0 ? (
                        (siteData?.sites || []).map((site) => (
                          <div key={site.institution_id} className="py-4 border-b border-white/5 last:border-b-0">
                            <p className="text-white font-medium mb-2">
                              {site.institution_name}
                              {site.city && <span className="text-white/30 text-sm font-normal"> — {site.city}, {site.state}</span>}
                            </p>
                            <div className="space-y-2">
                              {site.documents.map((doc) => (
                                <div key={doc.id} className="flex items-center justify-between pl-4">
                                  <span className="text-white/50 text-sm">{doc.document_type.replace(/_/g, ' ')}</span>
                                  <div className="flex items-center gap-3">
                                    <span className={`px-2 py-0.5 text-xs uppercase ${docStatusStyle(doc.status)}`}>{doc.status.replace(/_/g, ' ')}</span>
                                    {doc.status === 'draft' && regulatoryStudies.find(s => s.id === selectedStudyId)?.mine && (
                                      <button
                                        onClick={() => handleSubmitDoc(doc.id)}
                                        disabled={regActionId === doc.id}
                                        className="px-2 py-0.5 border border-white/20 text-white/60 text-xs uppercase hover:bg-white hover:text-black transition-all disabled:opacity-50"
                                      >
                                        Submit
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-6 text-center">
                          <p className="text-white/40 text-sm">No participating sites yet</p>
                          <p className="text-white/30 text-xs mt-1">Add institutions to run this study across multiple centers.</p>
                        </div>
                      )}
                    </div>

                    {/* Study Team */}
                    <div className="card-glass p-6 mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm uppercase tracking-wider text-white/40">Study Team</h3>
                        {regulatoryStudies.find(s => s.id === selectedStudyId)?.mine && (
                          <button
                            onClick={handleInviteCollaborator}
                            className="px-4 py-2 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all"
                          >
                            + Invite Collaborator
                          </button>
                        )}
                      </div>
                      {team.length > 0 ? (
                        <div className="space-y-2">
                          {team.map((member) => (
                            <div key={member.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                              <div>
                                <p className="text-white/80 text-sm">{member.user?.name || member.email}</p>
                                <p className="text-white/30 text-xs">
                                  {member.role.replace(/_/g, ' ')}
                                  {member.user?.organization && ` • ${member.user.organization}`}
                                </p>
                              </div>
                              <span className={`px-2 py-0.5 text-xs uppercase ${member.status === 'accepted' ? 'bg-[#00d4aa]/20 text-[#00d4aa]' : 'bg-amber-500/20 text-amber-500'}`}>
                                {member.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/40 text-sm">No collaborators yet. Invite researchers from other institutions to work on this study.</p>
                      )}
                    </div>

                    {regulatoryStudies.find(s => s.id === selectedStudyId)?.mine && (
                      <div className="card-glass p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm uppercase tracking-wider text-white/40">SYNTHETIC EXTRACTION</h3>
                          <button
                            onClick={handleRequestExtract}
                            disabled={extractActionId === 'create'}
                            className="px-4 py-2 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all disabled:opacity-50"
                          >
                            Request Test Extract
                          </button>
                        </div>
                        {extractionJobs.length > 0 ? (
                          <div className="space-y-2">
                            {extractionJobs.map((job) => (
                              <div key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-white/5 last:border-b-0">
                                <div className="min-w-0">
                                  <p className="text-white/80 text-sm break-all">{job.job_name}</p>
                                  <p className="text-white/30 text-xs">
                                    {job.patient_count || 0} test profile{job.patient_count === 1 ? '' : 's'} • {new Date(job.created_at).toLocaleDateString()}
                                  </p>
                                  {job.disclosure_risk && (
                                    <p className="text-white/40 text-xs mt-1">
                                      Re-identification check: smallest group k={job.disclosure_risk.min_k} of {job.disclosure_risk.threshold_k} required
                                      {job.disclosure_risk.meets_threshold ? ' — met' : ' — not met'}
                                    </p>
                                  )}
                                  {['failed', 'held_for_review'].includes(job.status) && job.error_message && (
                                    <p className="text-amber-300/80 text-xs mt-1">{job.error_message}</p>
                                  )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                  <span className={`px-2 py-0.5 text-xs uppercase ${jobStatusStyle(job.status)}`}>{jobStatusLabel(job.status)}</span>
                                  {job.status === 'completed' && job.download_url && (
                                    <button
                                      onClick={() => handleDownloadExtract(job)}
                                      disabled={extractActionId === job.id}
                                      className="px-3 py-1 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-all disabled:opacity-50"
                                    >
                                      Download CSV
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-white/40 text-sm">No test extracts yet. The simulation requires approved pilot gate states before generating synthetic output.</p>
                        )}
                      </div>
                    )}

                    {/* Shared with me */}
                    {collaborations.length > 0 && (
                      <div className="card-glass p-6">
                        <h3 className="text-sm uppercase tracking-wider text-white/40 mb-4">Shared With Me</h3>
                        <div className="space-y-2">
                          {collaborations.map((c) => (
                            <div key={c.study_id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
                              <div>
                                <p className="text-white/80 text-sm">{c.study_name}</p>
                                <p className="text-white/30 text-xs">
                                  PI: {c.pi_name}{c.pi_organization && ` (${c.pi_organization})`} • your role: {c.my_role.replace(/_/g, ' ')} • {c.site_count} site{c.site_count === 1 ? '' : 's'}
                                </p>
                              </div>
                              <span className="px-2 py-0.5 text-xs uppercase bg-white/10 text-white/40">{c.status.replace(/_/g, ' ')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Variable Selector Modal */}
      <AnimatePresence>
        {showVariableSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVariableSelector(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-black border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h2 className="text-xl text-white">Select Variables</h2>
                  <p className="text-white/40 text-sm mt-1">{selectedVariables.length} variables selected</p>
                </div>
                <button onClick={() => setShowVariableSelector(false)} className="p-2 hover:bg-white/10">
                  <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {Object.keys(variableCategories).length === 0 && (
                  <p className="text-white/40 text-sm">
                    No variables available yet. They appear here once enough consented patients have
                    contributed records
                    {variableInventory?.min_cell_size
                      ? ` — fields held by fewer than ${variableInventory.min_cell_size} patients are withheld`
                      : ''}.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(variableCategories).map(([category, variables]) => (
                    <div key={category}>
                      <h3 className="text-sm uppercase tracking-wider text-white/40 mb-3">
                        {category.replace(/_/g, ' ')}
                      </h3>
                      <div className="space-y-2">
                        {variables.map((variable) => (
                          <label 
                            key={variable.id}
                            className="flex items-center gap-3 cursor-pointer group"
                          >
                            <input
                              type="checkbox"
                              checked={selectedVariables.includes(variable.id)}
                              onChange={() => toggleVariable(variable.id)}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#00d4aa] focus:ring-[#00d4aa] focus:ring-offset-0"
                            />
                            <span className="text-white/60 text-sm group-hover:text-white transition-colors">
                              {variable.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-6 border-t border-white/10 flex justify-between">
                <button
                  onClick={() => setSelectedVariables(Object.values(variableCategories).flat().map(v => v.id))}
                  className="text-white/40 text-sm hover:text-white transition-colors"
                >
                  Select All
                </button>
                <div className="flex gap-4">
                  <button
                    onClick={() => setShowVariableSelector(false)}
                    className="px-6 py-3 border border-white/20 text-white text-xs uppercase tracking-wider hover:bg-white/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowVariableSelector(false)}
                    className="px-6 py-3 bg-[#00d4aa] text-black text-xs uppercase tracking-wider font-medium hover:bg-[#00d4aa]/90 transition-colors"
                  >
                    Confirm Selection
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResearcherDashboard;
